import { api, getAccessToken, type PackEstimate } from '../api';
import { callout, openDialog } from '../dialog';
import { openPdfViewer } from './pdf-viewer';
import { esc, html, toast } from '../ui';

/**
 * What a work pack will cost, shown before it is sent to anybody.
 *
 * The figures are on the screen first and the PDF second, on purpose: this is a
 * document that goes to a customer, and the moment to notice that the labour is
 * missing or a part has no price is before it leaves the building, not after.
 *
 * Reachable from the planning list and from inside the pack, because those are
 * the two moments somebody wants it — sizing a job up, and answering an owner
 * who has asked what it will come to.
 */

const money = (n: number, currency: string) =>
  `${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

/** Why the labour figure is what it is, said plainly. */
function labourNote(e: PackEstimate): string {
  if (e.labour.rate === null) {
    return 'No engineering hourly rate is set on the rate card, so labour cannot be priced. ' +
      'Set one on Organisation info before sending this.';
  }
  if (e.labour.from === 'none') {
    return 'No hours are recorded against this pack or its items, so there is no labour in this total. ' +
      'It covers parts only.';
  }
  if (e.labour.from === 'pack') {
    return 'Hours come from the estimate for the visit as a whole, because the individual items do not ' +
      'carry any.';
  }
  return 'Hours are the sum of the items’ own estimates, rounded up to the half hour.';
}

export function openEstimate(workOrderId: string, reference: string): void {
  openDialog<never>({
    wide: true,
    title: `Estimate for ${reference}`,
    sub: 'What this pack will cost, before it is worked',
    // Nothing to submit — the dialog is a reader with its own buttons.
    primary: '',
    body: () => html`<div data-estimate-body class="muted">Working it out…</div>`,
    onRender: (root) => {
      const body = root.querySelector<HTMLElement>('[data-estimate-body]');
      if (!body) return;

      void api.planning
        .estimate(workOrderId)
        .then((e) => {
          body.innerHTML = html`
            <div class="rows">
              ${e.labour.net > 0 && e.labour.rate !== null
                ? html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">
                          Labour — ${e.labour.hours} h at ${esc(money(e.labour.rate, e.currency))}/h
                        </div>
                        <div class="m">${esc(labourNote(e))}</div>
                      </div>
                      <b>${esc(money(e.labour.net, e.currency))}</b>
                    </div>
                  `
                : html`
                    <div class="row" style="border-left-color:var(--caution)">
                      <div class="bd">
                        <div class="t">No labour in this estimate</div>
                        <div class="m">${esc(labourNote(e))}</div>
                      </div>
                    </div>
                  `}

              ${e.parts.length
                ? html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">Parts — ${e.parts.length} line(s)</div>
                        <div class="m">
                          ${esc(e.parts.slice(0, 4).map((p) => `${p.description} ×${p.quantity}`).join('; '))}
                          ${e.parts.length > 4 ? ` and ${e.parts.length - 4} more` : ''}
                        </div>
                      </div>
                      <b>${esc(money(e.partsNet, e.currency))}</b>
                    </div>
                  `
                : ''}
            </div>

            <div class="card-f" style="margin-top:14px">
              Net <b>${esc(money(e.net, e.currency))}</b>${e.vatRate
                ? ` · VAT at ${e.vatRate}% ${esc(money(e.vat, e.currency))}`
                : ''} ·
              <b>Estimated total ${esc(money(e.gross, e.currency))}</b>
              ${e.exchangeRate
                ? html`<div class="tiny dim" style="margin-top:4px">
                    Converted from ${esc(e.pricedIn)} at 1 ${esc(e.pricedIn)} =
                    ${e.exchangeRate.toFixed(4)} ${esc(e.currency)}
                  </div>`
                : ''}
            </div>

            ${e.unpricedParts
              ? callout('caution', '!', html`
                  ${e.unpricedParts} part(s) have no price on file. They appear on the document as
                  <b>on application</b> and are not in the total — price them in the store first if the
                  customer needs a firm figure.
                `)
              : ''}

            ${callout('info', 'i', html`
              An estimate, not an invoice, and the document says so. Work found once the aircraft is
              opened up is not included — the notes on the PDF say that too, which is the sentence that
              saves the argument later.
            `)}

            <div style="display:flex;gap:8px;margin-top:14px">
              <button type="button" class="btn pri" data-estimate-pdf>Open the PDF</button>
            </div>
          `;

          body.querySelector<HTMLButtonElement>('[data-estimate-pdf]')?.addEventListener('click', () => {
            // Same viewer the CRS and invoices use: the access token lives in
            // memory, so a plain link would arrive without one.
            openPdfViewer({
              path: `/planning/work-orders/${workOrderId}/estimate/pdf`,
              title: `Estimate ${reference}`,
              subtitle: 'An estimate, not an invoice',
              accessToken: getAccessToken,
            });
          });
        })
        .catch((err: Error) => {
          body.innerHTML = html`<div class="callout c-caution">
            <span class="cg">!</span><div>${esc(err.message || 'That estimate could not be worked out.')}</div>
          </div>`;
          toast(err.message || 'That estimate could not be worked out.', 'error');
        });
    },
    collect: () => ({}) as never,
    submit: async () => undefined,
    done: () => '',
  });
}
